import { routePaths } from "../constants/routes";

export const interviewClusterLearningLinks = [
  { label: "Manual Testing Tutorial", to: routePaths.manualTesting },
  { label: "Automation Testing Tutorial", to: "/automation-testing-tutorial" },
  { label: "API Testing Tutorial", to: "/api-testing-tutorial" },
  { label: "Selenium Tutorial", to: "/selenium-tutorial" },
  { label: "API Status Codes", to: "/api-status-codes" },
  { label: "Join Live QA Course", to: routePaths.enroll },
];

function qa(question, answer) {
  return { question, answer };
}

function makeInterviewPage({
  slug,
  title,
  description,
  keyword,
  audience,
  sections,
}) {
  return {
    slug,
    path: `/${slug}`,
    eyebrow: "QA Interview Questions Cluster",
    title,
    description,
    keyword,
    audience,
    sections,
  };
}

export const interviewQuestionsClusterPages = [
  makeInterviewPage({
    slug: "testing-interview-questions",
    title: "Software Testing Interview Questions and Answers",
    description: "Prepare software testing interview questions for manual testing, automation, API testing, Selenium, test cases, defects, SQL basics, and HR rounds.",
    keyword: "Software Testing Interview Questions",
    audience: "freshers, manual testers, automation learners, and QA job seekers",
    sections: [
      {
        id: "qa-fundamentals",
        heading: "QA fundamentals",
        qas: [
          qa("What is software testing?", "Software testing is the process of verifying and validating that an application works as expected and meets business requirements."),
          qa("What is verification and validation?", "Verification checks whether we are building the product correctly. Validation checks whether we are building the correct product for users."),
          qa("What is SDLC?", "SDLC is the Software Development Life Cycle, which includes planning, requirement analysis, design, development, testing, deployment, and maintenance."),
          qa("What is STLC?", "STLC is the Software Testing Life Cycle, which includes requirement analysis, planning, test design, environment setup, execution, defect reporting, and closure."),
          qa("What is the difference between QA and QC?", "QA focuses on process improvement and defect prevention. QC focuses on product testing and defect identification."),
        ],
      },
      {
        id: "core-testing",
        heading: "Core testing questions",
        qas: [
          qa("What is manual testing?", "Manual testing is testing performed by a human tester without automation scripts by executing scenarios and comparing actual results with expected results."),
          qa("What is regression testing?", "Regression testing verifies that new code changes, fixes, or enhancements have not broken existing functionality."),
          qa("What is smoke testing?", "Smoke testing is a quick build verification test to check whether major features are working before deeper testing starts."),
          qa("What is sanity testing?", "Sanity testing verifies whether a specific fix or small changed area works as expected."),
          qa("What is exploratory testing?", "Exploratory testing is simultaneous learning, test design, and execution without strictly predefined test cases."),
        ],
      },
      {
        id: "job-ready",
        heading: "Job-ready scenario questions",
        qas: [
          qa("How will you test a login page?", "Test valid login, invalid password, unregistered email, blank fields, email format, password masking, forgot password, remember me, account lock, logout, and security basics."),
          qa("How will you report a bug?", "Include title, environment, steps to reproduce, actual result, expected result, screenshots or video, severity, priority, and affected build."),
          qa("What will you do if requirements are unclear?", "Ask clarifying questions, document assumptions, discuss with BA/product owner, and avoid writing final test cases until critical ambiguity is resolved."),
          qa("How do you decide what to test first?", "Prioritize business-critical flows, high-risk areas, frequently used features, recent changes, integrations, and defect-prone modules."),
          qa("How do you handle production defects?", "Reproduce the issue, collect logs/details, assess impact, communicate quickly, support fix verification, and add regression coverage after resolution."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "qa-interview-questions-fresher",
    title: "QA Interview Questions for Freshers",
    description: "Practice QA interview questions for freshers covering manual testing basics, SDLC, STLC, test cases, defects, severity, priority, and real examples.",
    keyword: "QA Interview Questions Fresher",
    audience: "freshers and beginners preparing for their first QA role",
    sections: [
      {
        id: "fresher-basics",
        heading: "Fresher basics",
        qas: [
          qa("Why do you want to become a QA tester?", "A strong answer connects curiosity, attention to detail, user empathy, logical thinking, and interest in improving software quality."),
          qa("What is a test case?", "A test case is a documented set of steps, test data, expected result, actual result, and status used to validate a feature."),
          qa("What is a test scenario?", "A test scenario is a high-level condition or user flow that should be tested."),
          qa("Test case vs test scenario?", "A test scenario says what to test. A test case explains how to test it with detailed steps and expected results."),
          qa("What is a defect?", "A defect is a mismatch between expected and actual behavior in the software."),
        ],
      },
      {
        id: "fresher-practical",
        heading: "Practical fresher questions",
        qas: [
          qa("Write test scenarios for a registration page.", "Check mandatory fields, valid registration, duplicate email, invalid email, weak password, confirm password mismatch, terms checkbox, captcha, and success message."),
          qa("What is severity?", "Severity is the impact of a defect on the application or business."),
          qa("What is priority?", "Priority is how soon a defect should be fixed."),
          qa("Give severity vs priority example.", "A typo on the homepage logo can be low severity but high priority. A rare crash in an unused admin report can be high severity but lower priority."),
          qa("What documents should a fresher know?", "Requirement document, test plan, test scenarios, test cases, defect report, RTM, and test summary report."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "qa-interview-questions-experienced",
    title: "QA Interview Questions for Experienced Testers",
    description: "Prepare experienced QA interview questions on test strategy, ownership, Agile QA, regression planning, defect metrics, stakeholder communication, and release quality.",
    keyword: "QA Interview Questions Experienced",
    audience: "QA engineers with project experience",
    sections: [
      {
        id: "experienced-strategy",
        heading: "Strategy and ownership",
        qas: [
          qa("How do you create a test strategy?", "Understand scope, risks, environments, data, timelines, automation scope, regression approach, entry criteria, exit criteria, and reporting needs."),
          qa("How do you handle tight deadlines?", "Use risk-based testing, prioritize critical flows, communicate tradeoffs, reduce low-value checks, and share clear coverage notes."),
          qa("How do you improve regression testing?", "Remove duplicate cases, prioritize critical paths, automate stable checks, tag suites, review failures, and keep regression data updated."),
          qa("How do you measure QA effectiveness?", "Use defect leakage, test coverage, automation pass rate, defect rejection rate, escaped defects, cycle time, and release confidence."),
          qa("How do you mentor junior testers?", "Review test cases, explain risk thinking, pair on defect analysis, provide examples, and help them understand business flows."),
        ],
      },
      {
        id: "experienced-scenarios",
        heading: "Experienced scenario questions",
        qas: [
          qa("What if developer says it is not a bug?", "Recheck requirement, reproduce clearly, provide evidence, discuss expected behavior, involve BA/product owner if needed, and stay objective."),
          qa("How do you manage unstable builds?", "Confirm build notes, run smoke tests first, report blockers early, avoid wasting full execution time, and request a stable deployment."),
          qa("How do you test without documentation?", "Explore the application, compare similar flows, ask stakeholders, identify business rules, document assumptions, and create exploratory charters."),
          qa("How do you decide automation candidates?", "Choose stable, repetitive, high-value, deterministic tests with clear expected results and manageable data setup."),
          qa("How do you handle production release sign-off?", "Share executed scope, passed/failed tests, open defects, risk areas, environment details, known issues, and recommendation."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "selenium-interview-questions",
    title: "Selenium Interview Questions and Answers",
    description: "Practice Selenium interview questions on WebDriver, locators, XPath, waits, frames, alerts, windows, Page Object Model, TestNG, and frameworks.",
    keyword: "Selenium Interview Questions",
    audience: "automation testers and Selenium learners",
    sections: [
      {
        id: "selenium-basics",
        heading: "Selenium basics",
        qas: [
          qa("What is Selenium?", "Selenium is an open-source suite of tools used to automate web browsers."),
          qa("What is Selenium WebDriver?", "WebDriver is an API that controls browsers programmatically and performs actions like click, type, select, and validate."),
          qa("What are locators in Selenium?", "Locators identify web elements. Common locators are ID, name, class name, tag name, link text, partial link text, CSS selector, and XPath."),
          qa("XPath vs CSS selector?", "XPath can traverse both forward and backward in the DOM, while CSS is usually faster and cleaner for styling-based element selection."),
          qa("What is Page Object Model?", "POM is a design pattern where page locators and actions are kept in page classes, while tests focus on test flow and assertions."),
        ],
      },
      {
        id: "selenium-advanced",
        heading: "Selenium practical questions",
        qas: [
          qa("What are waits in Selenium?", "Waits handle synchronization between the script and application. Main types are implicit wait, explicit wait, and fluent wait."),
          qa("How do you handle alerts?", "Switch to alert using driver.switchTo().alert(), then accept, dismiss, get text, or send input based on alert type."),
          qa("How do you handle frames?", "Switch into the frame by index, name/id, or WebElement, perform actions, then switch back to default content."),
          qa("How do you handle multiple windows?", "Store the parent window handle, get all window handles, switch to the required child window, perform actions, and return if needed."),
          qa("How do you reduce flaky tests?", "Use stable locators, explicit waits, clean test data, isolated tests, reliable environments, and avoid hard sleeps."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "api-interview-questions",
    title: "API Interview Questions and Answers",
    description: "Prepare API interview questions on REST, SOAP, HTTP methods, status codes, Postman, authentication, authorization, payloads, headers, and schema validation.",
    keyword: "API Interview Questions",
    audience: "manual testers, API testers, and automation learners",
    sections: [
      {
        id: "api-basics",
        heading: "API basics",
        qas: [
          qa("What is API testing?", "API testing validates backend endpoints by checking request, response, status code, headers, schema, authentication, and business rules."),
          qa("What is REST API?", "REST is an architectural style that uses HTTP methods and resources, commonly returning JSON responses."),
          qa("What is SOAP API?", "SOAP is a protocol that commonly uses XML messages and strict contracts through WSDL."),
          qa("GET vs POST?", "GET retrieves data and should not change server state. POST submits data and commonly creates a resource."),
          qa("PUT vs PATCH?", "PUT updates the full resource. PATCH updates selected fields."),
        ],
      },
      {
        id: "api-scenarios",
        heading: "API practical questions",
        qas: [
          qa("401 vs 403?", "401 means authentication is missing or invalid. 403 means the user is authenticated but does not have permission."),
          qa("How do you test a login API?", "Validate valid login, wrong password, missing fields, invalid email, blocked user, token generation, token expiry, and response schema."),
          qa("What is bearer token?", "A bearer token is an access token passed in the Authorization header to access protected APIs."),
          qa("What is schema validation?", "Schema validation checks whether response structure, field names, data types, and required fields match the expected contract."),
          qa("How do you test API performance?", "Check response time, throughput, error rate, load behavior, and bottlenecks using tools such as JMeter or similar tools."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "manual-testing-interview-questions",
    title: "Manual Testing Interview Questions and Answers",
    description: "Practice manual testing interview questions on SDLC, STLC, test cases, test scenarios, defects, severity, priority, smoke, sanity, and regression testing.",
    keyword: "Manual Testing Interview Questions",
    audience: "manual testing freshers and QA job seekers",
    sections: [
      {
        id: "manual-core",
        heading: "Manual testing core questions",
        qas: [
          qa("What is manual testing?", "Manual testing is testing performed by a human tester without automation scripts to validate software behavior."),
          qa("What is black box testing?", "Black box testing validates functionality without knowing internal code or implementation."),
          qa("What is white box testing?", "White box testing validates internal code, logic, paths, and structures."),
          qa("What is UAT?", "User Acceptance Testing is performed to confirm whether the application meets business needs and user expectations."),
          qa("What is retesting?", "Retesting verifies that a specific fixed defect now works correctly."),
        ],
      },
      {
        id: "manual-scenarios",
        heading: "Manual testing scenario questions",
        qas: [
          qa("Smoke vs sanity testing?", "Smoke testing checks major build stability. Sanity testing checks whether a specific fix or small change works."),
          qa("Regression vs retesting?", "Retesting checks a fixed defect. Regression checks whether existing features still work after a change."),
          qa("What is defect life cycle?", "Common defect stages include New, Assigned, Open, Fixed, Retest, Verified, Reopened, Deferred, Rejected, and Closed."),
          qa("How do you write good test cases?", "Use clear title, preconditions, test data, steps, expected result, priority, and traceability to requirement."),
          qa("What is RTM?", "Requirement Traceability Matrix maps requirements to test cases to ensure coverage."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "test-case-interview-questions",
    title: "Test Case Interview Questions and Answers",
    description: "Prepare test case interview questions with examples for login, registration, search, cart, payment, API, positive cases, negative cases, and boundary testing.",
    keyword: "Test Case Interview Questions",
    audience: "freshers and testers practicing test design",
    sections: [
      {
        id: "test-case-basics",
        heading: "Test case basics",
        qas: [
          qa("What is a test case?", "A test case is a detailed set of steps, data, and expected results used to validate a feature."),
          qa("What fields are included in a test case?", "Test case ID, title, precondition, test steps, test data, expected result, actual result, status, priority, and comments."),
          qa("What is a positive test case?", "A positive test case validates the system with valid input and expected user behavior."),
          qa("What is a negative test case?", "A negative test case validates how the system handles invalid input, missing data, or incorrect actions."),
          qa("What is boundary value testing?", "Boundary value testing checks values at the edges of valid and invalid ranges."),
        ],
      },
      {
        id: "test-case-examples",
        heading: "Test case example questions",
        qas: [
          qa("Write test cases for login page.", "Check valid login, invalid password, blank fields, invalid email format, locked account, forgot password, remember me, logout, and session timeout."),
          qa("Write test cases for search.", "Check exact keyword, partial keyword, empty search, special characters, no result, filters, sorting, pagination, and response time."),
          qa("Write test cases for payment.", "Check successful payment, failed payment, cancelled payment, invalid card, expired card, duplicate payment, timeout, refund, and receipt."),
          qa("Write API test cases for login.", "Validate method, endpoint, payload, status code, token, user data, missing fields, invalid credentials, blocked user, and response schema."),
          qa("How do you review test cases?", "Check coverage, clarity, expected results, data, negative cases, priority, duplicate cases, and requirement mapping."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "hr-interview-questions-qa",
    title: "HR Interview Questions for QA Testers",
    description: "Prepare HR interview questions for QA roles including self introduction, strengths, weaknesses, salary discussion, career goals, and project explanation.",
    keyword: "HR Interview Questions QA",
    audience: "QA freshers and experienced testers preparing for HR rounds",
    sections: [
      {
        id: "hr-common",
        heading: "Common HR questions",
        qas: [
          qa("Tell me about yourself.", "Give a short answer covering your background, testing skills, tools, project exposure, strengths, and why you are interested in the role."),
          qa("Why should we hire you?", "Connect your testing fundamentals, learning attitude, practical project work, communication, and ability to improve product quality."),
          qa("What are your strengths?", "Choose relevant strengths such as attention to detail, logical thinking, patience, clear communication, and ownership."),
          qa("What is your weakness?", "Share a real but manageable weakness and explain what you are doing to improve it."),
          qa("Why do you want to work in QA?", "Explain your interest in product quality, user experience, finding defects, understanding systems, and preventing production issues."),
        ],
      },
      {
        id: "hr-career",
        heading: "Career and offer questions",
        qas: [
          qa("Where do you see yourself in five years?", "Say you want to grow into a strong QA engineer with manual, API, automation, domain, and leadership skills."),
          qa("Why are you leaving your current company?", "Keep the answer positive: career growth, better learning, more responsibility, or stronger alignment with your goals."),
          qa("How do you handle pressure?", "Explain prioritization, communication, breaking work into smaller tasks, and focusing on risk-based testing."),
          qa("What salary do you expect?", "Give a flexible answer based on role, market standard, skills, and company policy."),
          qa("Do you have any questions for us?", "Ask about QA process, project domain, automation roadmap, team structure, release cycle, and learning opportunities."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "real-time-interview-questions",
    title: "Real-Time QA Interview Questions and Answers",
    description: "Practice real-time QA interview questions based on projects, Agile testing, defect handling, production issues, regression, API scenarios, and release sign-off.",
    keyword: "Real-Time QA Interview Questions",
    audience: "testers preparing for project-based QA interviews",
    sections: [
      {
        id: "project-scenarios",
        heading: "Project scenario questions",
        qas: [
          qa("What will you do if build is not stable?", "Run smoke checks, identify blockers, report immediately, avoid full execution on unstable build, and request a fixed build."),
          qa("What if a critical bug is found one day before release?", "Communicate impact, reproduce clearly, involve stakeholders, support fix verification, assess regression risk, and recommend release decision based on risk."),
          qa("How do you test when requirements keep changing?", "Track changes, update scenarios, clarify priority, focus on stable critical flows, and maintain change notes."),
          qa("How do you work in Agile?", "Participate in grooming, clarify stories, write scenarios early, test during sprint, report defects quickly, and support demos/regression."),
          qa("How do you explain your project?", "Explain domain, modules, your role, testing types, tools, test artifacts, major challenges, defects found, and business impact."),
        ],
      },
      {
        id: "real-time-api-automation",
        heading: "API and automation scenarios",
        qas: [
          qa("How do you decide test coverage?", "Use requirements, risk, user flows, integrations, data variations, history of defects, and business priority."),
          qa("How do you handle flaky automation tests?", "Analyze root cause, improve waits, stabilize test data, fix locators, isolate tests, and remove environment dependency."),
          qa("How do you validate backend without UI?", "Use API testing, database checks where allowed, logs, contract validation, and service-level test cases."),
          qa("What is your release checklist?", "Smoke pass, critical regression pass, open defect review, environment check, test summary, known issues, and sign-off recommendation."),
          qa("How do you test third-party integrations?", "Validate request/response, auth, error handling, timeout, retries, logs, fallback behavior, and contract changes."),
        ],
      },
    ],
  }),
  makeInterviewPage({
    slug: "top-100-qa-interview-questions",
    title: "Top 100 QA Interview Questions and Answers",
    description: "Prepare top QA interview questions across manual testing, automation, Selenium, API testing, test cases, SQL basics, Agile QA, and HR rounds.",
    keyword: "Top 100 QA Interview Questions",
    audience: "all QA learners preparing for interviews",
    sections: [
      {
        id: "top-manual-api",
        heading: "Top manual and API questions",
        qas: [
          qa("What is software testing?", "Software testing verifies and validates software quality against requirements and user expectations."),
          qa("What is test plan?", "A test plan defines scope, strategy, resources, schedule, risks, environments, and deliverables."),
          qa("What is bug life cycle?", "It is the flow of a defect from New to Assigned, Open, Fixed, Retest, Verified, Reopened, or Closed."),
          qa("What is API testing?", "API testing validates backend endpoints by checking status code, response body, headers, schema, auth, and business rules."),
          qa("What are common HTTP status codes?", "200 OK, 201 Created, 204 No Content, 400 Bad Request, 401 Unauthorized, 403 Forbidden, 404 Not Found, and 500 Server Error."),
        ],
      },
      {
        id: "top-automation-hr",
        heading: "Top automation and HR questions",
        qas: [
          qa("What is automation testing?", "Automation testing uses scripts and tools to execute repeatable test cases automatically."),
          qa("What is Selenium WebDriver?", "WebDriver is a browser automation API used to control browsers and interact with web elements."),
          qa("What is Page Object Model?", "POM separates page locators/actions from test logic to make automation easier to maintain."),
          qa("Tell me about yourself.", "Summarize your background, QA skills, tools, projects, strengths, and interest in the role."),
          qa("Why should we hire you?", "Explain your testing fundamentals, project practice, communication, learning mindset, and ability to improve quality."),
        ],
      },
    ],
  }),
];

export function getInterviewQuestionsClusterPage(slug) {
  return interviewQuestionsClusterPages.find((page) => page.slug === slug);
}
